import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaStar, FaRegStar } from 'react-icons/fa';
import { ProjectDTO } from '../../../DTO/ProjectDTO';
import ProjectEditForm from './ProjectEditForm';
import { StatusLabel } from '../../StatusLabel';

const StarIcon    = FaStar    as React.ElementType;
const RegStarIcon = FaRegStar as React.ElementType;

const ProjectTable: React.FC = () => {
    const [projects, setProjects]   = useState<ProjectDTO[]>([]);
    const [editing, setEditing]     = useState<ProjectDTO | null>(null);
    const [confirmId, setConfirmId] = useState<number | null>(null);

    useEffect(() => {
        axios.get<ProjectDTO[]>('/api/projects').then(r => setProjects(r.data));
    }, []);

    const handleDelete = async (id: number) => {
        await axios.delete(`/api/projects/${id}`);
        setProjects(prev => prev.filter(p => p.id !== id));
        setConfirmId(null);
    };

    const handleUpdate = (updated: ProjectDTO) => {
        setProjects(prev => prev.map(p => p.id === updated.id ? updated : p));
    };

    const toggleFeatured = async (id: number) => {
        await axios.post(`/api/projects/${id}/toggle-featured`);
        setProjects(prev => prev.map(p => p.id === id ? { ...p, featured: !p.featured } : p));
    };

    return (
        <>
            <div className="bg-surface rounded border border-border overflow-hidden">
                <table className="min-w-full text-sm">
                    <thead className="bg-paper font-label text-sm text-muted">
                    <tr>
                        <th className="px-4 py-3 text-left">Name</th>
                        <th className="px-4 py-3 text-left">Status</th>
                        <th className="px-4 py-3 text-left">Visible</th>
                        <th className="px-4 py-3 text-left">Featured</th>
                        <th className="px-4 py-3 text-left">Actions</th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                    {projects.length === 0 && (
                        <tr>
                            <td colSpan={5} className="px-4 py-6 text-center text-text-secondary">
                                No projects yet.
                            </td>
                        </tr>
                    )}
                    {projects.map(p => (
                        <tr key={p.id} className="hover:bg-background transition-colors">
                            <td className="px-4 py-3 font-medium text-on-surface">{p.name}</td>
                            <td className="px-4 py-3"><StatusLabel status={p.status} /></td>
                            <td className="px-4 py-3">
                                <span className={`font-label text-sm font-medium ${p.visible ? 'text-forest' : 'text-muted'}`}>
                                    {p.visible ? 'Visible' : 'Hidden'}
                                </span>
                            </td>
                            <td className="px-4 py-3">
                                <button
                                    onClick={() => toggleFeatured(p.id)}
                                    className="text-gentian hover:text-gentian-dark text-lg transition-colors"
                                    title={p.featured ? 'Remove from featured' : 'Mark as featured'}
                                >
                                    {p.featured ? <StarIcon /> : <RegStarIcon />}
                                </button>
                            </td>
                            <td className="px-4 py-3 flex gap-2">
                                <button
                                    onClick={() => setEditing(p)}
                                    className="btn-secondary btn-sm"
                                >
                                    Edit
                                </button>
                                <button
                                    onClick={() => setConfirmId(p.id)}
                                    className="btn-danger btn-sm"
                                >
                                    Delete
                                </button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            {/* Edit modal */}
            {editing && (
                <ProjectEditForm
                    project={editing}
                    onUpdated={handleUpdate}
                    onClose={() => setEditing(null)}
                />
            )}

            {/* Delete confirmation modal */}
            {confirmId !== null && (
                <div className="fixed inset-0 bg-ink/50 flex items-center justify-center z-50">
                    <div className="bg-surface border border-border rounded p-6 w-full max-w-sm shadow-md">
                        <h3 className="text-lg font-semibold text-on-surface mb-2">Delete Project</h3>
                        <p className="text-text-secondary mb-6">
                            Are you sure you want to delete <strong className="text-on-surface">
                                {projects.find(p => p.id === confirmId)?.name}
                            </strong>? This cannot be undone.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setConfirmId(null)}
                                className="px-4 py-2 rounded border border-border text-on-surface hover:bg-background transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleDelete(confirmId)}
                                className="btn-danger"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ProjectTable;
